import type { SetupRingPage } from '@/components/setup/SetupRing/SetupRing.vue'
import {
  type SetupRunErrorMessages,
  type SetupRunStageLines,
  runFailureMessageKey,
} from '@/components/setup/setup-run'
import { JOURNEY_PHASES, type JourneyPhase, phaseOfQuestion } from '@/types/brand'
import type {
  BeliefQuestionKey,
  ContentStrategyErrorCode,
  ContentStrategyQuestionEntry,
  ContentStrategyRefusalReason,
  ContentStrategyRun,
} from '@/types/content-strategy-run'
import { isRunFailed, isRunSettled } from '@/types/setup-run'

/** The line under a failed draft for each error code the run can end with. */
export const CONTENT_STRATEGY_ERROR_MESSAGES = {
  content_strategy_enqueue_failed: 'setup.contentStrategy.reading.errors.enqueueFailed',
  content_strategy_failed: 'setup.contentStrategy.reading.errors.draftFailed',
  content_strategy_customer_profile_missing: 'setup.contentStrategy.reading.errors.profileMissing',
  content_strategy_facts_missing: 'setup.contentStrategy.reading.errors.factsMissing',
  run_timed_out: 'setup.contentStrategy.reading.errors.timedOut',
} as const satisfies Record<ContentStrategyErrorCode, string> & SetupRunErrorMessages

/** The line under a draft that could not start. */
export const CONTENT_STRATEGY_START_FAILED_KEY = 'setup.contentStrategy.reading.errors.startFailed'

/** The i18n key of the line under a failed draft, or under a draft that could not start. */
export function contentStrategyFailureMessageKey(
  run: ContentStrategyRun | null,
  startFailed: boolean,
): string {
  if (startFailed || run === null || !isRunFailed(run)) return CONTENT_STRATEGY_START_FAILED_KEY
  return runFailureMessageKey(run, CONTENT_STRATEGY_ERROR_MESSAGES)
}

/** The message shown for each reason the server refuses a content-strategy call with. */
export const CONTENT_STRATEGY_REFUSAL_MESSAGES = {
  content_strategy_run_in_progress: 'setup.contentStrategy.refusals.runInProgress',
  content_strategy_customer_profile_missing: 'setup.contentStrategy.refusals.profileMissing',
  belief_map_missing: 'setup.contentStrategy.refusals.beliefMapMissing',
  obvious_choice_belief_missing: 'setup.contentStrategy.refusals.coreMessageMissing',
  content_strategy_missing: 'setup.contentStrategy.refusals.strategyMissing',
  belief_map_replaces_too_long: 'setup.contentStrategy.refusals.replacesTooLong',
  owner_admin_role_required: 'setup.contentStrategy.refusals.ownerAdminRequired',
  editor_role_required: 'setup.contentStrategy.refusals.editorRequired',
} as const satisfies Record<ContentStrategyRefusalReason, string>

/** The line under the ring for the core message stage; the beliefs stage names its live phase. */
export const CONTENT_STRATEGY_STAGE_LINES = {
  'core-message': 'setup.contentStrategy.reading.stages.core',
} as const satisfies SetupRunStageLines

/** The i18n key of the line under the ring while a question of each phase is written. */
export const CONTENT_STRATEGY_PHASE_LINES = {
  awareness: 'setup.contentStrategy.reading.stages.awareness',
  consideration: 'setup.contentStrategy.reading.stages.consideration',
  decision: 'setup.contentStrategy.reading.stages.decision',
} as const satisfies Record<JourneyPhase, string>

/** The i18n key of the chip under the ring for each journey phase. */
export const CONTENT_STRATEGY_STAGE_CHIPS = {
  awareness: 'setup.contentStrategy.reading.chips.awareness',
  consideration: 'setup.contentStrategy.reading.chips.consideration',
  decision: 'setup.contentStrategy.reading.chips.decision',
} as const satisfies Record<JourneyPhase, string>

/** The i18n key of the chip under the ring for the core message. */
export const CONTENT_STRATEGY_CORE_CHIP = 'setup.contentStrategy.reading.chips.core'

/** The i18n key of the line under the ring while this entry's question is written. */
export function contentStrategyEntryLineKey(entry: ContentStrategyQuestionEntry): string {
  return CONTENT_STRATEGY_PHASE_LINES[phaseOfQuestion(entry.questionKey)]
}

function phaseState(run: ContentStrategyRun, phase: JourneyPhase): SetupRingPage['state'] {
  const entries = run.entries.filter((entry) => phaseOfQuestion(entry.questionKey) === phase)
  const settled = entries.every((entry) => entry.step === 'done' || entry.step === 'failed')
  if (entries.length > 0 && settled) return 'done'
  return entries.some((entry) => entry.step === 'running') ? 'live' : 'todo'
}

/** The chips under the ring: one per phase, done once its questions are settled, then the core message. */
export function contentStrategyRingPages(
  run: ContentStrategyRun,
  label: (key: string) => string,
): SetupRingPage[] {
  const core: SetupRingPage['state'] = isRunSettled(run)
    ? 'done'
    : run.stage === 'core-message'
      ? 'live'
      : 'todo'
  return [
    ...JOURNEY_PHASES.map((phase) => ({
      label: label(CONTENT_STRATEGY_STAGE_CHIPS[phase]),
      state: phaseState(run, phase),
    })),
    { label: label(CONTENT_STRATEGY_CORE_CHIP), state: core },
  ]
}

/** The i18n key of each stage's objective, which follows its name. */
export const CONTENT_STRATEGY_PHASE_OBJECTIVES = {
  awareness: 'setup.contentStrategy.stages.awareness.objective',
  consideration: 'setup.contentStrategy.stages.consideration.objective',
  decision: 'setup.contentStrategy.stages.decision.objective',
} as const satisfies Record<JourneyPhase, string>

/** The i18n key of the line saying what each stage's content does, after the reader's question. */
export const CONTENT_STRATEGY_PHASE_NOTES = {
  awareness: 'setup.contentStrategy.stages.awareness.note',
  consideration: 'setup.contentStrategy.stages.consideration.note',
  decision: 'setup.contentStrategy.stages.decision.note',
} as const satisfies Record<JourneyPhase, string>

/** The i18n keys of the content formats each stage uses, in the order they show. */
export const CONTENT_STRATEGY_PHASE_FORMATS = {
  awareness: [
    'setup.contentStrategy.formats.howTo',
    'setup.contentStrategy.formats.research',
    'setup.contentStrategy.formats.pointOfView',
  ],
  consideration: [
    'setup.contentStrategy.formats.comparison',
    'setup.contentStrategy.formats.customerStory',
    'setup.contentStrategy.formats.productExplainer',
  ],
  decision: [
    'setup.contentStrategy.formats.customerStory',
    'setup.contentStrategy.formats.objection',
    'setup.contentStrategy.formats.pricing',
    'setup.contentStrategy.formats.ourStory',
  ],
} as const satisfies Record<JourneyPhase, readonly string[]>

/** The i18n key of the subject each belief question covers. */
export const CONTENT_STRATEGY_QUESTION_SUBJECTS = {
  'awareness.problem': 'setup.contentStrategy.subjects.problem',
  'awareness.prevalence': 'setup.contentStrategy.subjects.prevalence',
  'awareness.cost': 'setup.contentStrategy.subjects.cost',
  'consideration.options': 'setup.contentStrategy.subjects.options',
  'consideration.our-option': 'setup.contentStrategy.subjects.ourOption',
  'consideration.fit': 'setup.contentStrategy.subjects.fit',
  'decision.brand': 'setup.contentStrategy.subjects.brand',
  'decision.offer': 'setup.contentStrategy.subjects.offer',
  'decision.after-purchase': 'setup.contentStrategy.subjects.afterPurchase',
} as const satisfies Record<BeliefQuestionKey, string>
